import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BlockNotiz } from "../../notizbloecke/blockModell";
import {
    besterTreffer,
    oeffneFaden,
    springeZu,
    sprungAus,
    sprungZiel,
} from "./sprungmarke";

/** So lange wird nach der Stelle gesucht — das To-do muss erst aufklappen. */
const SUCHE_MS = 3000;
/** So lange wird auf den BESTEN Treffer (den Beitrag selbst) gewartet. */
const GEDULD_MS = 1200;
const TAKT_MS = 100;

export type SprungmarkeOptionen = {
    suchParameter: URLSearchParams;
    setSuchParameter: (
        next: (vorher: URLSearchParams) => URLSearchParams,
        opts?: { replace?: boolean },
    ) => void;
    /** Vorgang UND Verlauf sind geladen — erst dann ist „fehlt" eine Aussage. */
    bereit: boolean;
    notizen: BlockNotiz[];
    todoIds: number[];
    zeigeHinweis: (text: string) => void;
    /**
     * Die Seite hält seit dem Plan „Vorgangsdetail mobil" (PR 1), welche
     * To-dos offen sind — sie erfährt den Sprung hierüber und klappt selbst
     * auf. Der Rückgabewert (Aufklapp-Zähler) bleibt für Karten, die ihren
     * Zustand selbst führen.
     */
    onAufklappen?: (todoId: number) => void;
};

/**
 * `?todo=` / `?notiz=` der Vorgangsansicht (User 01.11.2026): To-do
 * aufklappen, zur Notiz bzw. zum Kommentar scrollen, kurz golden hervorheben
 * und die Parameter danach aus der URL nehmen (`replace`) — ein Neuladen
 * springt nicht erneut. Alte Links ohne Parameter berührt das nicht.
 *
 * Liefert je To-do den Aufklapp-Zähler für `TodoKarteV4.aufklappen`.
 */
export function useSprungmarke({
    suchParameter,
    setSuchParameter,
    bereit,
    notizen,
    todoIds,
    zeigeHinweis,
    onAufklappen,
}: SprungmarkeOptionen): (todoId: number) => number | undefined {
    const todoRoh = suchParameter.get("todo");
    const notizRoh = suchParameter.get("notiz");
    const sprung = useMemo(() => {
        const p = new URLSearchParams();
        if (todoRoh) p.set("todo", todoRoh);
        if (notizRoh) p.set("notiz", notizRoh);
        return sprungAus(p);
    }, [todoRoh, notizRoh]);
    const [aufklappen, setAufklappen] = useState<{ todoId: number; n: number } | null>(null);
    /** Welche Marke schon bearbeitet ist — der Effekt läuft sonst bei jedem Nachladen. */
    const erledigtRef = useRef<string | null>(null);
    const aufraeumenRef = useRef<(() => void) | null>(null);

    // Aktuelle Werte für den laufenden Takt, ohne ihn neu zu starten.
    const notizenRef = useRef(notizen);
    notizenRef.current = notizen;
    const todoIdsRef = useRef(todoIds);
    todoIdsRef.current = todoIds;
    const hinweisRef = useRef(zeigeHinweis);
    hinweisRef.current = zeigeHinweis;
    const aufklappenRef = useRef(onAufklappen);
    aufklappenRef.current = onAufklappen;

    const parameterWeg = useCallback(() => {
        setSuchParameter(
            (vorher) => {
                const naechste = new URLSearchParams(vorher);
                naechste.delete("todo");
                naechste.delete("notiz");
                return naechste;
            },
            { replace: true },
        );
    }, [setSuchParameter]);

    useEffect(() => {
        if (!sprung || !bereit) return;
        const marke = `${sprung.todoId ?? ""}|${sprung.notiz ?? ""}`;
        if (erledigtRef.current === marke) return;
        erledigtRef.current = marke;

        const ziel = sprungZiel(sprung, notizenRef.current);
        if (ziel.todoId != null && todoIdsRef.current.includes(ziel.todoId)) {
            setAufklappen((v) => ({ todoId: ziel.todoId!, n: (v?.n ?? 0) + 1 }));
            aufklappenRef.current?.(ziel.todoId);
        }
        if (ziel.fehlt) {
            hinweisRef.current("Die Notiz gibt es nicht mehr — sie wurde gelöscht oder verschoben.");
        }

        const start = Date.now();
        let fadenAuf = false;
        let fertig = false;
        const beenden = (el: HTMLElement | null) => {
            if (fertig) return;
            fertig = true;
            window.clearInterval(takt);
            if (el) {
                aufraeumenRef.current?.();
                aufraeumenRef.current = springeZu(el);
            }
            parameterWeg();
        };
        const suche = () => {
            if (fertig) return;
            const wurzel = document.querySelector(".vg4-page") ?? document;
            const t = besterTreffer(wurzel, ziel.kandidaten);
            const vergangen = Date.now() - start;
            if (t && t.rang === 0) return beenden(t.el);
            // Der Kommentar steht hinter „n weitere": den Faden aufmachen und
            // im nächsten Takt erneut suchen.
            if (ziel.faden && !fadenAuf && t?.el.getAttribute("data-faden") === ziel.faden) {
                fadenAuf = true;
                oeffneFaden(ziel.faden);
                return;
            }
            if (t && vergangen >= GEDULD_MS) return beenden(t.el);
            if (vergangen >= SUCHE_MS) return beenden(t?.el ?? null);
        };
        const takt = window.setInterval(suche, TAKT_MS);
        // Gleich einmal versuchen: steht alles schon da, wartet niemand.
        window.setTimeout(suche, 0);
        return () => {
            if (!fertig) {
                fertig = true;
                window.clearInterval(takt);
                // Abgebrochen (Seite verlassen, anderer Vorgang): beim
                // nächsten Mal von vorn.
                erledigtRef.current = null;
            }
        };
    }, [sprung, bereit, parameterWeg]);

    useEffect(() => () => aufraeumenRef.current?.(), []);

    return useCallback(
        (todoId: number) => (aufklappen && aufklappen.todoId === todoId ? aufklappen.n : undefined),
        [aufklappen],
    );
}
